import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import './InterviewSetup.css';

const InterviewSetup = ({ onStartInterview, baselineTestId = null, onShowFeedback }) => {
  const { accessToken, sessionId, logout, email } = useAuth();
  const [resumes, setResumes] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [baselineTests, setBaselineTests] = useState([]);
  const [selectedResumeId, setSelectedResumeId] = useState('');
  const [selectedJobRoleId, setSelectedJobRoleId] = useState('');
  const [selectedBaselineTestId, setSelectedBaselineTestId] = useState(baselineTestId || '');
  const [selectedDifficulty, setSelectedDifficulty] = useState('medium');
  const [selectedDuration, setSelectedDuration] = useState(15);
  const [resumableEnabled, setResumableEnabled] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);

  // Check if this is a baseline assessment
  const isBaseline = !!selectedBaselineTestId;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch resumes, job roles, and baseline tests in parallel
      const [resumesData, jobRolesData, baselineTestsData] = await Promise.all([
        apiService.getMyResumes(accessToken, sessionId),
        apiService.getJobRoles(accessToken, sessionId),
        apiService.getBaselineTests(accessToken, sessionId)
      ]);

      setResumes(resumesData || []);
      setJobRoles(jobRolesData || []);
      setBaselineTests(baselineTestsData || []);

      // Auto-select first job role if available (resume is optional, so don't auto-select)
      // BUT: Don't auto-select if a baseline test is already selected from URL
      if (!selectedBaselineTestId && jobRolesData && jobRolesData.length > 0) {
        setSelectedJobRoleId(jobRolesData[0].id);
      }

      // If baselineTestId was passed via URL, find and select that test
      if (baselineTestId && baselineTestsData && baselineTestsData.length > 0) {
        const matchingTest = baselineTestsData.find(test => test.test_id === baselineTestId);
        if (matchingTest) {
          setSelectedBaselineTestId(matchingTest.test_id);
          // Auto-select the job role associated with this baseline test by matching title
          // TODO: Backend should return job_role_id in the API response
          if (matchingTest.job_role_title) {
            const matchingRole = jobRolesData.find(role => role.title === matchingTest.job_role_title);
            if (matchingRole) {
              setSelectedJobRoleId(matchingRole.id);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, sessionId, baselineTestId, selectedBaselineTestId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle baseline test selection
  const handleBaselineTestChange = (e) => {
    const testId = e.target.value;
    setSelectedBaselineTestId(testId);

    // If a baseline test is selected, auto-populate the job role by matching title
    // TODO: Backend should return job_role_id in the API response for direct matching
    if (testId) {
      const selectedTest = baselineTests.find(test => test.test_id === testId);
      if (selectedTest && selectedTest.job_role_title) {
        // Try to find matching job role by title
        const matchingRole = jobRoles.find(role => role.title === selectedTest.job_role_title);
        if (matchingRole) {
          setSelectedJobRoleId(matchingRole.id);
        }
      }
    }
  };

  const handleResumeFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a PDF or Word document');
        setResumeFile(null);
        e.target.value = null;
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setError('File size must be less than 10MB');
        setResumeFile(null);
        e.target.value = null;
        return;
      }

      setResumeFile(file);
      setError(null);
      // Clear selected resume if file is uploaded
      setSelectedResumeId('');
    }
  };

  const handleStartInterview = async () => {
    if (!selectedJobRoleId) {
      setError('Please select a job role');
      return;
    }

    setStarting(true);
    setError(null);

    try {
      // Call the parent handler with selected IDs, difficulty, duration, resumable flag, baselineTestId, and resume file
      // Resume ID can be null if not selected
      // For baseline assessments, difficulty is ignored by the backend
      // Voice is now handled by backend using INTERVIEW_VOICE_ID env variable
      await onStartInterview(
        selectedResumeId || null,
        selectedJobRoleId,
        selectedDifficulty,
        null, // voiceId is no longer used - backend uses INTERVIEW_VOICE_ID env variable
        selectedDuration,
        resumableEnabled,
        selectedBaselineTestId || null, // Pass the selected baseline test ID if this is a baseline assessment
        resumeFile // Pass the resume file if uploaded
      );
    } catch (err) {
      console.error('Failed to start interview:', err);
      setError(err.message || 'Failed to start interview');
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="interview-setup">
        <div className="setup-container">
          <h2>Loading your data...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="interview-setup">
      <div className="setup-container">
        <div className="setup-header">
          <div>
            <h1>{isBaseline ? 'Baseline Assessment Setup' : 'Setup Your Interview'}</h1>
            <p className="subtitle">
              {isBaseline
                ? 'This baseline assessment will help us understand your current skills and create a personalized training program for you'
                : 'Select the job role you\'re interviewing for (resume is optional)'}
            </p>
          </div>
          <div className="user-info">
            <span className="user-email">{email}</span>
            <button
              className="feedback-btn-setup"
              onClick={onShowFeedback}
              style={{
                padding: '6px 16px',
                background: 'rgba(102, 126, 234, 0.1)',
                border: '1px solid #667eea',
                borderRadius: '6px',
                color: '#667eea',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Share Interview Feedback
            </button>
            <button className="logout-btn-setup" onClick={logout}>
              Logout
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="selection-section">
          {/* Baseline Test Selector - shown at the top */}
          {baselineTests.length > 0 && (
            <div className="form-group">
              <label htmlFor="baseline-test-select">
                <strong>📊 Select Baseline Assessment (Optional):</strong>
              </label>
              <select
                id="baseline-test-select"
                value={selectedBaselineTestId}
                onChange={handleBaselineTestChange}
                disabled={starting}
                style={{
                  border: selectedBaselineTestId ? '2px solid #2196f3' : '1px solid #ccc',
                  background: selectedBaselineTestId ? '#e3f2fd' : 'white'
                }}
              >
                <option value="">-- Regular Interview (No Baseline) --</option>
                {baselineTests.map((test) => (
                  <option key={test.test_id} value={test.test_id}>
                    {test.job_role_title} - {test.status === 'completed' ? '✅ Completed' : '📝 In Progress'}
                  </option>
                ))}
              </select>
              <p className="helper-text">
                {selectedBaselineTestId
                  ? '✨ Baseline assessment mode enabled. This will help us create a personalized training program for you!'
                  : 'Choose a baseline test to assess your current skills, or leave blank for a regular interview.'}
              </p>
            </div>
          )}

          {isBaseline && (
            <div className="info-message" style={{
              background: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
              color: '#1565c0'
            }}>
              <strong>📊 Baseline Assessment Mode</strong>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
                This is a supportive assessment to understand your current capabilities. There are no wrong answers -
                just be honest about your current level so we can create the best training plan for you!
              </p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="resume-select">Select Resume (Optional):</label>
            <select
              id="resume-select"
              value={selectedResumeId}
              onChange={(e) => {
                setSelectedResumeId(e.target.value);
                // Clear file if selecting existing resume
                if (e.target.value && resumeFile) {
                  setResumeFile(null);
                }
              }}
              disabled={starting || !!resumeFile}
            >
              <option value="">-- No Resume (Interview based on job role only) --</option>
              {resumes.map((resume) => (
                <option key={resume.id} value={resume.id}>
                  {resume.originalName} (Uploaded: {new Date(resume.uploadedAt).toLocaleDateString()})
                </option>
              ))}
            </select>
            <p className="helper-text">
              {resumes.length === 0
                ? 'No resumes found. You can upload one below or start an interview without a resume.'
                : 'Selecting a resume will tailor questions to your background. Leave blank for general questions.'}
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="resume-file-upload">Or Upload New Resume (Optional):</label>
            <input
              type="file"
              id="resume-file-upload"
              accept=".pdf,.doc,.docx"
              onChange={handleResumeFileChange}
              disabled={starting || !!selectedResumeId}
              style={{
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                width: '100%',
                cursor: (starting || !!selectedResumeId) ? 'not-allowed' : 'pointer'
              }}
            />
            {resumeFile && (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#2196f3', fontSize: '14px' }}>
                  ✓ {resumeFile.name} ({(resumeFile.size / 1024).toFixed(2)} KB)
                </span>
                <button
                  type="button"
                  onClick={() => setResumeFile(null)}
                  disabled={starting}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    background: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
              </div>
            )}
            <p className="helper-text">
              Upload a PDF or Word document. The resume will be automatically parsed and questions will be tailored to your background.
              {selectedResumeId && ' (Clear the selected resume above to upload a new one)'}
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="jobrole-select">
              Select Job Role:
              {isBaseline && selectedJobRoleId && (
                <span style={{ color: '#2196f3', fontSize: '12px', marginLeft: '8px' }}>
                  (Auto-selected from baseline test)
                </span>
              )}
            </label>
            <select
              id="jobrole-select"
              value={selectedJobRoleId}
              onChange={(e) => setSelectedJobRoleId(e.target.value)}
              disabled={starting || (isBaseline && selectedJobRoleId)}
              style={{
                cursor: (isBaseline && selectedJobRoleId) ? 'not-allowed' : 'pointer',
                opacity: (isBaseline && selectedJobRoleId) ? 0.7 : 1
              }}
            >
              <option value="">-- Select a Job Role --</option>
              {jobRoles.map((jobRole) => (
                <option key={jobRole.id} value={jobRole.id}>
                  {jobRole.title} {jobRole.companyName ? `at ${jobRole.companyName}` : ''}
                </option>
              ))}
            </select>
            {jobRoles.length === 0 && (
              <p className="helper-text">No job roles found. Please create a job role first.</p>
            )}
            {isBaseline && selectedJobRoleId && (
              <p className="helper-text" style={{ color: '#2196f3' }}>
                This job role is linked to your baseline test and cannot be changed.
              </p>
            )}
          </div>

          {!isBaseline && (
            <>
              <div className="form-group">
                <label htmlFor="difficulty-select">Difficulty Level:</label>
                <select
                  id="difficulty-select"
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  disabled={starting}
                >
                  <option value="easy">Easy - 0-1 Yrs Experience</option>
                  <option value="medium">Medium - 1-3 Yrs Experience</option>
                  <option value="hard">Hard - 3+ Yrs Experience</option>
                </select>
                <p className="helper-text">Choose the difficulty level based on your experience.</p>
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="duration-select">Select Interview Duration:</label>
            <select
              id="duration-select"
              value={selectedDuration}
              onChange={(e) => setSelectedDuration(Number(e.target.value))}
              disabled={starting}
            >
              <option value="2">2 minutes - Test interview</option>
              <option value="10">10 minutes - Quick interview</option>
              <option value="15">15 minutes - Standard interview</option>
              <option value="20">20 minutes - Extended interview</option>
              <option value="30">30 minutes - Comprehensive interview</option>
              <option value="45">45 minutes - In-depth interview</option>
              <option value="60">60 minutes - Full interview</option>
            </select>
            <p className="helper-text">Choose how long the interview should last.</p>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                id="resumable-checkbox"
                checked={resumableEnabled}
                onChange={(e) => setResumableEnabled(e.target.checked)}
                disabled={starting}
              />
              <span className="checkbox-text">
                <strong>Enable Resume Feature</strong>
                <span className="checkbox-subtext">
                  Allow this interview to resume from where you left off if the page is refreshed or connection is lost
                </span>
              </span>
            </label>
          </div>
        </div>

        <div className="actions">
          <button
            className="start-interview-btn"
            onClick={handleStartInterview}
            disabled={!selectedJobRoleId || starting}
          >
            {starting
              ? (isBaseline ? 'Starting Assessment...' : 'Starting Interview...')
              : (isBaseline ? 'Start Baseline Assessment' : 'Start Interview')}
          </button>
        </div>

        {selectedJobRoleId && (
          <div className="selection-summary">
            <h3>{isBaseline ? 'Assessment Summary:' : 'Interview Summary:'}</h3>
            {isBaseline && (
              <p style={{ color: '#2196f3', fontStyle: 'italic', marginBottom: '12px' }}>
                ℹ️ This baseline assessment uses standardized settings to fairly evaluate all users
              </p>
            )}
            {isBaseline && selectedBaselineTestId && (
              <p><strong>Baseline Test:</strong> {
                (() => {
                  const test = baselineTests.find(t => t.test_id === selectedBaselineTestId);
                  return test ? `${test.job_role_title} - ${test.status === 'completed' ? 'Completed' : 'In Progress'}` : 'Selected';
                })()
              }</p>
            )}
            <p><strong>Resume:</strong> {
              resumeFile ? `📄 ${resumeFile.name} (New upload - will be parsed)` :
              selectedResumeId ? resumes.find(r => r.id === selectedResumeId)?.originalName :
              'None (General interview)'
            }</p>
            <p><strong>Job Role:</strong> {jobRoles.find(j => j.id === selectedJobRoleId)?.title}</p>
            {!isBaseline && (
              <>
                <p><strong>Interview Type:</strong> Comprehensive (Technical, Behavioral & Cultural Fit)</p>
                <p><strong>Difficulty:</strong> {
                  selectedDifficulty.charAt(0).toUpperCase() + selectedDifficulty.slice(1)
                }</p>
              </>
            )}
            {isBaseline && (
              <p><strong>Assessment Type:</strong> Baseline (Comprehensive evaluation across multiple dimensions)</p>
            )}
            <p><strong>Duration:</strong> {selectedDuration} minutes</p>
            <p><strong>Resume Feature:</strong> {resumableEnabled ? '✅ Enabled (interview will auto-resume on refresh)' : '❌ Disabled'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewSetup;
